/**
 * MetaOverview — lo que va ARRIBA del tablero cuando la fuente es Meta:
 *
 *  - AccountStrip: una sola línea con la ficha de la cuenta (estado, gasto
 *    histórico, límite de gasto) y el semáforo de salud de la entrega. Las
 *    alertas se despliegan bajo demanda: si todo está bien, ocupa una línea.
 *  - DailyTrendCard: la evolución DIARIA del periodo. El resto del tablero es
 *    mensual; esto es lo único que deja ver el pico de un lanzamiento o el día
 *    en que se acabó el presupuesto.
 */
import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaChevronDown, FaChartArea, FaTimesCircle,
} from 'react-icons/fa';
import { FaMeta } from 'react-icons/fa6';
import { compact, numFmt, pct, shortDate } from './metaAdsShared';
import './MetaAds.css';

const SEV_ICON = { error: <FaTimesCircle />, warn: <FaExclamationTriangle />, info: <FaInfoCircle /> };

export function AccountStrip({ account, health, money, loading }) {
  const [open, setOpen] = useState(false);
  if (!account && !health) {
    return loading ? <div className="mad-strip mad-strip-loading">Consultando la cuenta publicitaria…</div> : null;
  }
  const items = health?.items || [];
  const errors = items.filter((i) => i.severity === 'error').length;
  const warns = items.filter((i) => i.severity === 'warn').length;
  const infos = items.filter((i) => i.severity === 'info').length;
  const tone = errors ? 'bad' : warns ? 'warn' : infos ? 'info' : 'ok';
  const summary = [
    errors && `${errors} ${errors === 1 ? 'problema' : 'problemas'}`,
    warns && `${warns} ${warns === 1 ? 'alerta' : 'alertas'}`,
    infos && `${infos} en aprendizaje`,
  ].filter(Boolean).join(' · ');
  const capUse = account?.spendCap && account?.amountSpent != null ? account.amountSpent / account.spendCap : null;
  const active = account?.status === 1 || account?.status == null;

  return (
    <section className={`mad-strip tone-${tone}`}>
      <div className="mad-strip-row">
        <span className="mad-strip-icon"><FaMeta /></span>
        <div className="mad-strip-id">
          <strong>{account?.name || 'Cuenta publicitaria'}</strong>
          {account?.business && <span className="mad-strip-muted">{account.business}</span>}
        </div>
        <span className={`mad-chip ${active ? 'ok' : 'bad'}`}>{active ? 'Cuenta activa' : 'Cuenta inactiva'}</span>
        {account?.amountSpent != null && (
          <span className="mad-strip-fact" title="Gasto acumulado de la cuenta desde que se creó">
            <span className="l">Gasto histórico</span><span className="v">{money(account.amountSpent)}</span>
          </span>
        )}
        {capUse != null && (
          <span className="mad-strip-fact" title={`Límite de gasto: ${money(account.spendCap)}. Al llegar al 100 % Meta detiene los anuncios.`}>
            <span className="l">Límite de gasto</span>
            <span className="mad-cap"><span className="mad-cap-fill" style={{ width: `${Math.min(100, capUse * 100)}%`, background: capUse >= 0.9 ? '#ef4444' : capUse >= 0.75 ? '#f59e0b' : '#10b981' }} /></span>
            <span className="v">{pct(capUse, 0)}</span>
          </span>
        )}
        <span className="mad-strip-spacer" />
        {health && (
          items.length === 0 ? (
            health.complete === false
              // Una de las revisiones no respondió: no se puede decir "sin alertas".
              ? <span className="mad-health info" title="Meta no respondió una parte de la revisión (suele ser un límite temporal de consultas). Vuelve a intentar con Actualizar."><FaInfoCircle /> Revisión de la entrega incompleta</span>
              : <span className="mad-health ok"><FaCheckCircle /> Entrega sin alertas</span>
          ) : (
            <button type="button" className={`mad-health ${tone}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
              {SEV_ICON[errors ? 'error' : warns ? 'warn' : 'info']} {summary}
              <FaChevronDown className={`mad-chev ${open ? 'open' : ''}`} />
            </button>
          )
        )}
      </div>
      {open && items.length > 0 && (
        <ul className="mad-health-list">
          {items.map((i, idx) => (
            <li key={idx} className={`sev-${i.severity}`}>
              <span className="mad-health-ico">{SEV_ICON[i.severity]}</span>
              <div>
                <div className="mad-health-head">
                  <span className="mad-health-level">{i.level}</span>
                  <strong>{i.entity || '—'}</strong>
                  {i.campaign && i.campaign !== i.entity && <span className="mad-strip-muted">· {i.campaign}</span>}
                </div>
                <p>{i.message}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Métrica de la serie diaria: cómo se lee, cómo se agrega a la semana.
const DAILY_METRICS = [
  { id: 'spend', label: 'Inversión', color: '#073C5C', sum: true },
  { id: 'results', label: 'Resultados', color: '#10b981', sum: true, needs: 'results' },
  { id: 'impressions', label: 'Impresiones', color: '#3b82f6', sum: true },
  { id: 'clicks', label: 'Clics', color: '#F68D2E', sum: true },
  { id: 'ctr', label: 'CTR', color: '#8b5cf6', sum: false },
];
const WEEKLY_AFTER_DAYS = 120;

function weekStart(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const day = (d.getDay() + 6) % 7; // lunes = 0
  d.setDate(d.getDate() - day);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function DailyTrendCard({ daily, money, focusName, loading, resultLabel }) {
  const hasResults = daily.some((d) => d.results);
  const metrics = DAILY_METRICS.filter((m) => m.needs !== 'results' || hasResults);
  const [metricId, setMetricId] = useState('spend');
  const metric = metrics.find((m) => m.id === metricId) || metrics[0];
  const weekly = daily.length > WEEKLY_AFTER_DAYS;

  const series = useMemo(() => {
    if (!weekly) return daily.map((d) => ({ ...d, key: d.date }));
    const map = new Map();
    daily.forEach((d) => {
      const k = weekStart(d.date);
      const cur = map.get(k) || { key: k, date: k, spend: 0, impressions: 0, clicks: 0, results: 0 };
      cur.spend += d.spend || 0; cur.impressions += d.impressions || 0;
      cur.clicks += d.clicks || 0; cur.results += d.results || 0;
      map.set(k, cur);
    });
    return [...map.values()].map((w) => ({ ...w, ctr: w.impressions ? w.clicks / w.impressions : null }));
  }, [daily, weekly]);

  const values = series.map((d) => (d[metric.id] ?? 0));
  // Media móvil de 7 puntos: separa la tendencia del ruido de fin de semana.
  const avg = values.map((_, i) => {
    const win = values.slice(Math.max(0, i - 6), i + 1);
    return win.reduce((a, b) => a + b, 0) / win.length;
  });

  const stats = useMemo(() => {
    const withSpend = series.filter((d) => (d.spend || 0) > 0);
    const totalSpend = series.reduce((a, d) => a + (d.spend || 0), 0);
    const peak = withSpend.reduce((b, d) => (!b || d.spend > b.spend ? d : b), null);
    return { peak, activeDays: withSpend.length, avgSpend: withSpend.length ? totalSpend / withSpend.length : 0 };
  }, [series]);

  const fmtV = (v) => (metric.id === 'spend' ? money(v) : metric.id === 'ctr' ? pct(v) : numFmt.format(Math.round(v || 0)));
  const labelOf = (d) => new Date(`${d.date}T12:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }).replace('.', '');

  if (!daily.length) {
    return loading ? <section className="cmp-card col-12"><div className="mad-loading">Consultando la serie diaria…</div></section> : null;
  }

  return (
    <section className="cmp-card col-12">
      <div className="cmp-card-head">
        <span className="cmp-card-title"><FaChartArea /> Evolución {weekly ? 'semanal' : 'diaria'}{focusName ? ` · ${focusName}` : ''}</span>
        <div className="mad-seg" role="tablist">
          {metrics.map((m) => (
            <button key={m.id} type="button" className={m.id === metric.id ? 'active' : ''} onClick={() => setMetricId(m.id)}>
              {m.id === 'results' && resultLabel ? resultLabel : m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="cmp-card-body">
        <div className="mad-daily-stats">
          <span><b>{stats.activeDays}</b> {weekly ? 'semanas' : 'días'} con inversión</span>
          <span>Promedio por {weekly ? 'semana' : 'día'}: <b>{money(stats.avgSpend)}</b></span>
          {stats.peak && <span>{weekly ? 'Semana' : 'Día'} de mayor inversión: <b>{shortDate(stats.peak.date)}</b> ({money(stats.peak.spend)})</span>}
        </div>
        <div className="cmp-chart-box">
          <Bar
            data={{
              labels: series.map(labelOf),
              datasets: [
                { type: 'line', label: 'Tendencia (media de 7)', data: avg, borderColor: '#F68D2E', backgroundColor: '#F68D2E', borderWidth: 2, pointRadius: 0, tension: 0.35, order: 1 },
                { type: 'bar', label: metric.label, data: values, backgroundColor: `${metric.color}cc`, borderRadius: 3, order: 2, maxBarThickness: 18 },
              ],
            }}
            options={{
              responsive: true, maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: { position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 10, font: { size: 10 } } },
                tooltip: { callbacks: {
                  title: (it) => (series[it[0].dataIndex] ? `${weekly ? 'Semana del ' : ''}${shortDate(series[it[0].dataIndex].date)}` : ''),
                  label: (c) => `${c.dataset.label}: ${fmtV(c.parsed.y)}`,
                } },
              },
              scales: {
                x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 14 } },
                y: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { font: { size: 10 }, maxTicksLimit: 5, callback: (v) => (metric.id === 'ctr' ? `${(v * 100).toFixed(1)}%` : compact(v)) } },
              },
            }}
          />
        </div>
      </div>
    </section>
  );
}
